import { lazy, Suspense } from 'react'
import { Navigate, RouteObject } from 'react-router-dom'
import { UserRole, useAuthStore } from '../shared/stores/authStore'

// 布局组件
import MainLayout from '../shared/layouts/MainLayout'
import UserLayout from '../user/layouts/UserLayout'

// ============ 用户前端页面（懒加载） ============
const HomePage = lazy(() => import('../user/pages/HomePage'))
const NovelsPage = lazy(() => import('../user/pages/NovelsPage'))
const ReaderPage = lazy(() => import('../user/pages/ReaderPage'))
const ProfilePage = lazy(() => import('../user/pages/ProfilePage'))
const CreatePage = lazy(() => import('../user/pages/CreatePage'))
const WriterPage = lazy(() => import('../user/pages/WriterPage'))
const AgentDashboardPage = lazy(() => import('../user/pages/AgentDashboardPage'))
const KGDashboardPage = lazy(() => import('../user/pages/KGDashboardPage'))
const TemplatesPage = lazy(() => import('../user/pages/TemplatesPage'))
const BookshelfPage = lazy(() => import('../user/pages/BookshelfPage'))
const ChapterVersionPage = lazy(() => import('../user/pages/ChapterVersionPage'))
const ExportPage = lazy(() => import('../user/pages/ExportPage'))
const WritingProgressPage = lazy(() => import('../user/pages/WritingProgressPage'))
const PricingPage = lazy(() => import('../user/pages/PricingPage'))
const SubscriptionPage = lazy(() => import('../user/pages/SubscriptionPage'))
const InspirationCapturePage = lazy(() => import('../user/pages/InspirationCapturePage'))
const CreditsPage = lazy(() => import('../user/pages/CreditsPage'))
const AudiobooksPage = lazy(() => import('../user/pages/AudiobooksPage'))
const MarketplacePage = lazy(() => import('../user/pages/MarketplacePage'))
const CheckinCenter = lazy(() => import('../user/pages/CheckinCenter'))
const MemoryDashboardPage = lazy(() => import('../user/pages/MemoryDashboardPage'))
const AuthorPage = lazy(() => import('../user/pages/author/AuthorPage'))

// ============ Admin 页面（懒加载） ============
const LoginPage = lazy(() => import('../admin/pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('../admin/pages/auth/RegisterPage'))
const ApplyAuthorPage = lazy(() => import('../admin/pages/auth/LoginPage'))
const SSOCallbackPage = lazy(() => import('../admin/pages/auth/SSOCallbackPage'))
const NotFoundPage = lazy(() => import('../admin/pages/error/NotFoundPage'))
const SettingsPage = lazy(() => import('../user/pages/SettingsPage'))
const DashboardPage = lazy(() => import('../admin/pages/dashboard/DashboardPage'))

// 创作管理
const NovelManagementPage = lazy(() => import('../admin/pages/creation/NovelManagementPage'))
const ChapterManagementPage = lazy(() => import('../admin/pages/creation/ChapterManagementPage'))
const CharacterManagementPage = lazy(() => import('../admin/pages/creation/CharacterManagementPage'))
const WorldManagementPage = lazy(() => import('../admin/pages/creation/WorldManagementPage'))
const TaskManagementPage = lazy(() => import('../admin/pages/creation/TaskManagementPage'))

// 系统管理
const UserManagementPage = lazy(() => import('../admin/pages/management/UserManagementPage'))
const AuthorManagementPage = lazy(() => import('../admin/pages/management/AuthorManagementPage'))
const NovelReviewPage = lazy(() => import('../admin/pages/management/NovelReviewPage'))
const CommentManagementPage = lazy(() => import('../admin/pages/management/CommentManagementPage'))

// 系统设置
const SystemConfigPage = lazy(() => import('../admin/pages/system/SystemConfigPage'))
const SystemMonitoringPage = lazy(() => import('../admin/pages/system/SystemMonitoringPage'))
const SystemLogsPage = lazy(() => import('../admin/pages/system/SystemLogsPage'))
const ApiManagementPage = lazy(() => import('../admin/pages/system/ApiManagementPage'))
const SecuritySettingsPage = lazy(() => import('../admin/pages/system/SecuritySettingsPage'))
const AiModelManagementPage = lazy(() => import('../admin/pages/system/AiModelManagementPage'))
const LDAPConfigPage = lazy(() => import('../admin/pages/system/LDAPConfigPage'))
const TenantManagementPage = lazy(() => import('../admin/pages/system/TenantManagementPage'))
const SSOConfigPage = lazy(() => import('../admin/pages/system/SSOConfigPage'))
const CreditSystemConfigPage = lazy(() => import('../admin/pages/system/CreditSystemConfigPage'))
const MarketplaceAdminPage = lazy(() => import('../admin/pages/system/MarketplaceAdminPage'))
const TTSConfigPage = lazy(() => import('../admin/pages/system/TTSConfigPage'))
const ContentApiConfigPage = lazy(() => import('../admin/pages/system/ContentApiConfigPage'))
const DatabaseConfigPage = lazy(() => import('../admin/pages/system/DatabaseConfigPage'))

// 改写系统
const RewriteUploadPage = lazy(() => import('../admin/pages/rewrite/RewriteUploadPage'))
const RewriteDashboardPage = lazy(() => import('../admin/pages/rewrite/RewriteDashboardPage'))
const RewriteReviewPage = lazy(() => import('../admin/pages/rewrite/RewriteReviewPage'))
const RewriteHistoryPage = lazy(() => import('../admin/pages/rewrite/RewriteHistoryPage'))
const CollectPage = lazy(() => import('../admin/pages/rewrite/CollectPage'))

// ============ 商业化运营页面（懒加载） ============
const AdManagementPage = lazy(() => import('../admin/pages/monetization/AdManagementPage'))
const SplashAdManagementPage = lazy(() => import('../admin/pages/monetization/SplashAdManagementPage'))
const AudiobookManagementPage = lazy(() => import('../admin/pages/monetization/AudiobookManagementPage'))

// ============ 开发者门户页面（懒加载） ============
const DevDashboard = lazy(() => import('../devportal/pages/Dashboard'))
const DevProjects = lazy(() => import('../devportal/pages/Projects'))
const DevProjectDetail = lazy(() => import('../devportal/pages/ProjectDetail'))
const DevAPIKeys = lazy(() => import('../devportal/pages/APIKeys'))
const DevDocumentation = lazy(() => import('../devportal/pages/Documentation'))
const DevSupport = lazy(() => import('../devportal/pages/Support'))
const DevSettings = lazy(() => import('../devportal/pages/Settings'))
const DevKnowledgeBase = lazy(() => import('../devportal/pages/KnowledgeBase'))
const DevAnalytics = lazy(() => import('../devportal/pages/Analytics'))
const DevAIManagement = lazy(() => import('../devportal/pages/AIManagement'))
const DevContentApi = lazy(() => import('../devportal/pages/ContentApi'))

// ============ 组件 ============

/** 全局懒加载加载指示器 */
// 社区页面
const ForumsPage = lazy(() => import('../user/pages/community/ForumsPage'))
const PostListPage = lazy(() => import('../user/pages/community/PostListPage'))
const PostDetailPage = lazy(() => import('../user/pages/community/PostDetailPage'))
const CreatePostPage = lazy(() => import('../user/pages/community/CreatePostPage'))
const ReviewsPage = lazy(() => import('../user/pages/community/ReviewsPage'))
const ReviewDetailPage = lazy(() => import('../user/pages/community/ReviewDetailPage'))

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
      <p className="mt-4 text-gray-600">加载中...</p>
    </div>
  </div>
)

/** 通用 Suspense 包裹 */
const LazyLoad = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
)

/** 受保护路由组件
 *  - 处理 loading / 未登录 / 无权限三种状态
 *  - requiredRoles 为空时仅检查登录状态
 */
interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: UserRole[]
}

const ProtectedRoute = ({ children, requiredRoles }: ProtectedRouteProps) => {
  const { isAuthenticated, user, isLoading } = useAuthStore()

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (!isAuthenticated) {
    // 未登录 -> 跳转到统一登录页，保留当前 URL 以便登录后回跳
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
    return <Navigate to={`/login?redirect=${returnUrl}`} replace />
  }

  // 角色权限检查
  if (requiredRoles && requiredRoles.length > 0 && user) {
    if (!requiredRoles.includes(user.role as UserRole)) {
      // 无权限 -> 重定向到 dashboard（管理员）或 home（普通用户）
      return <Navigate to="/home" replace />
    }
  }

  return <>{children}</>
}

// ============ 路由配置 ============

/** 公开路由 — 无需认证，无布局包装 */
const publicRoutes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/home" replace />,
  },
  {
    path: '/login',
    element: (
      <LazyLoad>
        <LoginPage />
      </LazyLoad>
    ),
  },
  {
    path: '/admin/login',
    element: (
      <LazyLoad>
        <LoginPage />
      </LazyLoad>
    ),
  },
  {
    path: '/register',
    element: (
      <LazyLoad>
        <RegisterPage />
      </LazyLoad>
    ),
  },
  {
    path: '/apply-author',
    element: (
      <LazyLoad>
        <ApplyAuthorPage />
      </LazyLoad>
    ),
  },
  {
    path: '/auth/callback',
    element: (
      <LazyLoad>
        <SSOCallbackPage />
      </LazyLoad>
    ),
  },
  {
    path: '/404',
    element: (
      <LazyLoad>
        <NotFoundPage />
      </LazyLoad>
    ),
  },
  // /asset-market 是 /marketplace 的别名
  {
    path: '/asset-market',
    element: <Navigate to="/marketplace" replace />,
  },
  // /points 是 /credits 的别名
  {
    path: '/points',
    element: <Navigate to="/credits" replace />,
  },
  // /shop 是 /marketplace 的别名
  {
    path: '/shop',
    element: <Navigate to="/marketplace" replace />,
  },
]

/** 用户前端路由 — UserLayout 包装
 *  - /home, /novels, /novels/:id/read 对所有访问者可见（无需登录）
 *  - /profile 需要认证
 */
const userRoutes: RouteObject[] = [
  {
    element: <UserLayout />,
    children: [
      {
        path: '/home',
        element: (
          <LazyLoad>
            <HomePage />
          </LazyLoad>
        ),
      },
      {
        path: '/novels',
        element: (
          <LazyLoad>
            <NovelsPage />
          </LazyLoad>
        ),
      },
      {
        path: '/novels/:id/read',
        element: (
          <LazyLoad>
            <ReaderPage />
          </LazyLoad>
        ),
      },
      {
        path: '/bookshelf',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <BookshelfPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/profile',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <ProfilePage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/settings',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <SettingsPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/create',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <CreatePage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/write',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <WriterPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/write/:novelId',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <WriterPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/write/:novelId/:chapterId',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <WriterPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/chapters/:chapterId/versions',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <ChapterVersionPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/agents',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <AgentDashboardPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/kg',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <KGDashboardPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/templates',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <TemplatesPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/writing/progress',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <WritingProgressPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/export',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <ExportPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/community',
        element: (
          <LazyLoad>
            <ForumsPage />
          </LazyLoad>
        ),
      },
      {
        path: '/community/forum/:forumId',
        element: (
          <LazyLoad>
            <PostListPage />
          </LazyLoad>
        ),
      },
      {
        path: '/community/post/:postId',
        element: (
          <LazyLoad>
            <PostDetailPage />
          </LazyLoad>
        ),
      },
      {
        path: '/community/create',
        element: (
          <LazyLoad>
            <CreatePostPage />
          </LazyLoad>
        ),
      },
      {
        path: '/reviews',
        element: (
          <LazyLoad>
            <ReviewsPage />
          </LazyLoad>
        ),
      },
      {
        path: '/reviews/:id',
        element: (
          <LazyLoad>
            <ReviewDetailPage />
          </LazyLoad>
        ),
      },
      {
        path: '/pricing',
        element: (
          <LazyLoad>
            <PricingPage />
          </LazyLoad>
        ),
      },
      {
        path: '/subscription',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <SubscriptionPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/export/:novelId',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <ExportPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/inspiration',
        element: (
          <LazyLoad>
            <InspirationCapturePage />
          </LazyLoad>
        ),
      },
      {
        path: '/credits',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <CreditsPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/audiobooks',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <AudiobooksPage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/marketplace',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <MarketplacePage />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/checkin',
        element: (
          <ProtectedRoute>
            <LazyLoad>
              <CheckinCenter />
            </LazyLoad>
          </ProtectedRoute>
        ),
      },
      {
        path: '/memory',
        element: (
          <LazyLoad>
            <MemoryDashboardPage />
          </LazyLoad>
        ),
      },
      {
        path: '/author/:username',
        element: (
          <LazyLoad>
            <AuthorPage />
          </LazyLoad>
        ),
      },
    ],
  },
]

/** 管理后台路由 — MainLayout + 认证 + 角色权限 */
const adminRoutes: RouteObject[] = [
  {
    path: '/admin',
    element: (
      <ProtectedRoute
        requiredRoles={[UserRole.WRITER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}
      >
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/admin/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <LazyLoad>
            <DashboardPage />
          </LazyLoad>
        ),
      },

      // ---- 创作管理（作者 / 管理员） ----
      {
        path: 'creation',
        children: [
          {
            path: 'novels',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.WRITER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <NovelManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'chapters',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.WRITER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <ChapterManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'characters',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.WRITER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <CharacterManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'worlds',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.WRITER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <WorldManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'tasks',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.WRITER, UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <TaskManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
        ],
      },

      // ---- 改写系统（仅管理员/超级管理员） ----
      {
        path: 'rewrite',
        children: [
          {
            path: 'upload',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <RewriteUploadPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'dashboard',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <RewriteDashboardPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'review',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <RewriteReviewPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'history',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <RewriteHistoryPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'collect',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <CollectPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
        ],
      },

      // ---- 商业化运营（仅管理员 / 超级管理员） ----
      {
        path: 'monetization',
        children: [
          {
            path: 'ads',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <AdManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'ads/splash',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <SplashAdManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'audiobooks',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <AudiobookManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
        ],
      },

      // ---- 系统管理（仅管理员 / 超级管理员） ----
      {
        path: 'management',
        children: [
          {
            path: 'users',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <UserManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'authors',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <AuthorManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'novels',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <NovelReviewPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'comments',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <CommentManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
        ],
      },

      // ---- 系统设置（仅管理员 / 超级管理员） ----
      {
        path: 'system',
        children: [
          {
            path: 'config',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <SystemConfigPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'monitoring',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <SystemMonitoringPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'logs',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <SystemLogsPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'api',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <ApiManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'security',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <SecuritySettingsPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'ai-models',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <AiModelManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'ldap',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <LDAPConfigPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'tenants',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <TenantManagementPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'sso',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <SSOConfigPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'credits',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <CreditSystemConfigPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'marketplace',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <MarketplaceAdminPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'tts',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <TTSConfigPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'content-api',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <ContentApiConfigPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
          {
            path: 'database',
            element: (
              <ProtectedRoute
                requiredRoles={[UserRole.ADMIN, UserRole.SUPER_ADMIN]}
              >
                <LazyLoad>
                  <DatabaseConfigPage />
                </LazyLoad>
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
]

/** 开发者门户路由 — MainLayout + 任何登录用户即可访问 */
const devPortalRoutes: RouteObject[] = [
  {
    path: '/dev',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/dev/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: (
          <LazyLoad>
            <DevDashboard />
          </LazyLoad>
        ),
      },
      {
        path: 'projects',
        element: (
          <LazyLoad>
            <DevProjects />
          </LazyLoad>
        ),
      },
      {
        path: 'projects/:projectId',
        element: (
          <LazyLoad>
            <DevProjectDetail />
          </LazyLoad>
        ),
      },
      {
        path: 'api-keys',
        element: (
          <LazyLoad>
            <DevAPIKeys />
          </LazyLoad>
        ),
      },
      {
        path: 'documentation',
        element: (
          <LazyLoad>
            <DevDocumentation />
          </LazyLoad>
        ),
      },
      {
        path: 'support',
        element: (
          <LazyLoad>
            <DevSupport />
          </LazyLoad>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyLoad>
            <DevSettings />
          </LazyLoad>
        ),
      },
      {
        path: 'knowledge-base',
        element: (
          <LazyLoad>
            <DevKnowledgeBase />
          </LazyLoad>
        ),
      },
      {
        path: 'analytics',
        element: (
          <LazyLoad>
            <DevAnalytics />
          </LazyLoad>
        ),
      },
      {
        path: 'ai-management',
        element: (
          <LazyLoad>
            <DevAIManagement />
          </LazyLoad>
        ),
      },
      {
        path: 'content-api',
        element: (
          <LazyLoad>
            <DevContentApi />
          </LazyLoad>
        ),
      },
    ],
  },
]

/**
 * 完整路由配置
 *
 * 匹配顺序：
 * 1. PUBLIC 路由（/login, /admin/login, /auth/callback, /, /404）
 * 2. USER 路由（UserLayout → /home, /novels, /novels/:id/read, /profile）
 * 3. ADMIN 路由（MainLayout + 角色校验 → /admin/*）
 * 4. DEVPORTAL 路由（MainLayout + 登录校验 → /dev/*）
 * 5. 通配符 → 重定向到 /404
 */
const routes: RouteObject[] = [
  ...publicRoutes,
  ...userRoutes,
  ...adminRoutes,
  ...devPortalRoutes,
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
]

export default routes
